<template>
    <div>
        <h5 class="link-header geist-font mx-4" id="overview">Overview</h5>
        <div class="section px-4 mt-6 d-flex">
            <div class="mr-11">
                <VDropdown
                    placement="top"
                    :triggers="['hover']"
                    :popper-triggers="['hover']"
                    :inert="false"
                >
                    <p
                        class="geist-font semi-bold dungeonDepths--text mb-6 tooltip-indicator d-inline-block"
                    >
                        Claim Breadth
                    </p>
                    <template #popper>
                        <div class="popover-container pa-2">
                            {{ t('definitionTooltips.patentClaimBreadth.definition') }}
                        </div>
                    </template>
                </VDropdown>
                <MetricSlider
                    :label="props.patentDetails?.claimBreadth"
                    :value="claimBreadthPercentage"
                    :disabled="props.patentDetails?.claimBreadth == 'Not Scored'"
                />
            </div>
            <div class="mr-11">
                <VDropdown
                    placement="top"
                    :triggers="['hover']"
                    :popper-triggers="['hover']"
                    :inert="false"
                >
                    <p
                        class="geist-font semi-bold dungeonDepths--text mb-4 tooltip-indicator d-inline-block"
                    >
                        Validity Score
                    </p>
                    <template #popper>
                        <div class="popover-container pa-2">
                            {{ t('definitionTooltips.validity.definition') }}
                        </div>
                    </template>
                </VDropdown>
                <MetricPill
                    :steps="[{ label: 'Low' }, { label: 'Med' }, { label: 'High' }]"
                    :current-value="props.patentDetails?.validity"
                    :disabled="props.patentDetails?.validity == 'Not Ranked'"
                />
            </div>
            <div class="d-flex align-items-center flex-wrap">
                <p class="geist-font semi-bold dungeonDepths--text mb-4">Litigation Cases</p>
                <div class="w-full stat-wrapper d-flex">
                    <div class="my-1 case-stat d-flex align-items-center" style="width: 120px">
                        <p class="geist-font knights-cloak--text align-left mr-4">PTAB</p>
                        <AonSkeleton
                            class="w-full d-flex align-items-center"
                            :loading="props.loadingPatentDetail"
                            :data="0"
                            :height="'20px'"
                            light
                        >
                            <p class="geist-font knights-cloak--text semi-bold">
                                {{ formatCaseCount(props.patentDetails?.itcCaseCount) }}
                            </p>
                        </AonSkeleton>
                    </div>
                    <div class="my-1 case-stat d-flex align-items-center" style="width: 120px">
                        <p class="geist-font knights-cloak--text align-left mr-4">ITC</p>
                        <AonSkeleton
                            class="w-full d-flex align-items-center"
                            :loading="props.loadingPatentDetail"
                            :data="0"
                            :height="'20px'"
                            light
                        >
                            <p class="geist-font knights-cloak--text semi-bold">
                                {{ formatCaseCount(props.patentDetails?.itcCaseCount) }}
                            </p>
                        </AonSkeleton>
                    </div>
                    <div class="my-1 case-stat d-flex align-items-center" style="width: 120px">
                        <p class="geist-font knights-cloak--text align-left mr-4">DC</p>
                        <AonSkeleton
                            class="w-full d-flex align-items-center"
                            :loading="props.loadingPatentDetail"
                            :data="0"
                            :height="'20px'"
                            light
                        >
                            <p class="geist-font knights-cloak--text semi-bold">
                                {{ formatCaseCount(props.patentDetails?.patentDcCaseCount) }}
                            </p>
                        </AonSkeleton>
                    </div>
                </div>
            </div>
        </div>

        <!-- Inventors -->
        <h6
            v-if="
                (!props.loadingPatentDetail && props.patentDetails?.inventorDetails?.length > 0) ||
                props.loadingPatentDetail
            "
            class="geist-font section-header bold knightsCloak--text mb-1 pl-4 mt-4"
        >
            Inventors
        </h6>
        <div class="section px-4 mt-2 d-flex flex-wrap">
            <AonSkeleton
                class="w-full d-flex align-items-center"
                :loading="props.loadingPatentDetail"
                :data="0"
                :height="'30px'"
                light
            >
                <p
                    class="geist-font knightsCloak--text"
                    v-if="!props.patentDetails?.inventorDetails"
                >
                    No inventors Found
                </p>
                <div v-else class="inventor-holder d-flex align-items-center flex-wrap">
                    <p
                        v-for="inventor in props.patentDetails.inventorDetails"
                        class="geist-font mr-2 mb-1"
                    >
                        {{ inventor.inventorName }},
                    </p>
                </div>
            </AonSkeleton>
        </div>

        <!-- Abstract -->
        <h6 class="geist-font section-header bold knightsCloak--text mb-1 px-4 mt-4">Abstract</h6>
        <div class="section px-4 mt-2 mb-4">
            <div class="abstract-holder d-inline">
                <AonSkeleton
                    class="w-full d-flex align-items-center"
                    :loading="props.loadingPatentDetail"
                    :data="0"
                    :height="'80px'"
                    light
                >
                    <p class="geist-font knightsCloak--text" v-if="!props.patentDetails?.abstract">
                        No abstract found
                    </p>
                    <p v-else-if="!showAllAbstract" class="geist-font castle-stone-700--text small">
                        {{ trimmedAbstract }}
                        <span class="link small" @click="showAllAbstract = !showAllAbstract">
                            Show More
                        </span>
                    </p>
                    <p v-else class="geist-font castle-stone-700--text small">
                        {{ props.patentDetails?.abstract }}
                        <span class="link small" @click="showAllAbstract = !showAllAbstract">
                            Show Less
                        </span>
                    </p>
                </AonSkeleton>
            </div>
        </div>
    </div>
</template>

<script setup>
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import MetricSlider from '@/components/helpers/MetricSlider.vue'
import MetricPill from '@/components/helpers/MetricPill.vue'

const props = defineProps({
    patentDetails: {
        type: Object,
        default: () => ({}),
    },
    loadingPatentDetail: {
        type: Boolean,
        default: false,
    },
})

const { t } = useI18n()
const showAllAbstract = ref(false)

const claimBreadthPercentage = computed(() => {
    if (props.patentDetails?.claimBreadth === 'Very Broad') {
        return 100
    } else if (props.patentDetails?.claimBreadth === 'Broad') {
        return 66
    } else if (props.patentDetails?.claimBreadth === 'Average') {
        return 33
    } else if (props.patentDetails?.claimBreadth === 'Narrow') {
        return 0
    }
})

const trimmedAbstract = computed(() => {
    if (props.patentDetails?.abstract?.length > 500) {
        return `${props.patentDetails.abstract.slice(0, 500).replace(/\\n/g, ' ').trimEnd()}...`
    } else {
        return props.patentDetails?.abstract
            ? `${props.patentDetails.abstract.slice(0, 500).replace(/\\n/g, ' ').trimEnd()}`
            : ''
    }
})

const formatCaseCount = (caseCount) => {
    return (caseCount ?? 0).toLocaleString()
}
</script>

<style lang="scss" scoped>
@forward '@/styles/new_styles.scss';

.tooltip-indicator {
    cursor: help;
}

.popover-container {
    max-width: 300px;
}

.abstract-holder {
    min-height: 80px;
}

.link {
    color: var(--navy01);
    cursor: pointer;

    &:hover {
        text-decoration: underline;
    }
}
</style>
